import './Layout.css';
import { newRequestId } from '../../data/requestId';
import { sendAiRequest } from '../../data/aiRequests';

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import supabase from '../../supabaseClient/supabaseClient';
import { useAuth } from '../Login/AuthContext';
import Texteditor from '../Texteditor/Texteditor.jsx';
import TemplateCarousel from '../TemplateCarousel/TemplateCarousel.js';
import Popup from '../MobilePopUp/Popup.jsx';
import Navbar from '../Navbar/navbar';

import { generateCountingQuestions } from '../Texteditor/TemplatePrintersTE/generateCountingQuestions.js';
import generatePicOpsQuestions from '../Texteditor/TemplatePrintersTE/generatePictureOps.js';

import PictureOperations from '../Templates/PictureOperations/PictureOperations.jsx';
import Conversiontemplate from '../Templates/OnetoOneConversion/OnetoOneConversion.jsx';
import Columnquestions from '../Templates/ColumnQuestions/Columnquestions.js';
import ExamRevision from '../Templates/ExamRevision/aiprompt.jsx';
import CountingWorksheetTemplate from '../Templates/CountingQuestions/CountingQuestions.jsx';
import CustomPrompt from '../CustomPrompt/CustomPrompt.jsx';

// Keep template components and direct routes for future prompt-based selection.
const SHOW_TEMPLATE_CHOOSER = false;

export const TEMPLATE_NAMES = {
  'exam-style': 'Exam Style Revision',
  'column-template': 'Column Operations',
  'conversion-template': 'Fractions, Decimals & Percentages',
  'counting-template': 'Counting for Kids',
  'picture-ops-template': 'Addition & Subtraction with Pictures',
};

export const postAuthenticatedGeneration = sendAiRequest;

function Layout() {
  const { user } = useAuth();
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryMessage, setRetryMessage] = useState('');
  const showRetry = ({ retry, maxRetries }) => setRetryMessage(`Generation interrupted. Retrying (${retry}/${maxRetries})…`);
  // Only used for custom-prompt generations, which stay on the bare
  // /create route (no :templateId) — template-driven generations read
  // the template id straight from the URL param instead.
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // 'initial' | 'template-config' | 'generating' | 'editing'
  const [appStage, setAppStage] = useState(templateId ? 'template-config' : 'initial');
  const [userPrompt, setUserPrompt] = useState('');
  const [generationSettings, setGenerationSettings] = useState(null);
  // The params that produced userPrompt — kept so the editable prompt box
  // shown during editing (see render below) can be pre-filled with them.
  // region null: the prompt box uses the teacher's default region.
  const [promptParams, setPromptParams] = useState({ region: null, difficulty: null, topics: [] });
  const [promptMode, setPromptMode] = useState(false);
  const [showStartOverWarning, setShowStartOverWarning] = useState(false);
  const lastGenerationRef = useRef(null);
  const hasRedirectedRef = useRef(false);

  const activeTemplateId = templateId || selectedTemplate;
  const isEditorVisible = appStage === 'generating' || appStage === 'editing';
  const pendingNavigationRef = useRef(null);

  // The URL is the source of truth for which template we're creating with.
  // /create/:templateId doesn't remount this component when the param
  // changes (e.g. picking a different template), so reset local
  // generation state whenever it does.
  useEffect(() => {
    setAppStage(templateId ? 'template-config' : 'initial');
    setFilteredQuestions([]);
    setError('');
    setPromptMode(false);
    setGenerationSettings(null);
    setUserPrompt('');
    setPromptParams({ region: null, difficulty: null, topics: [] });
    setSelectedTemplate(null);
    lastGenerationRef.current = null;
    hasRedirectedRef.current = false;
  }, [templateId]);

  // Native browser confirmation for closing the tab / refreshing / typing a
  // new URL — the one case in-app navigation guarding below can't cover.
  useEffect(() => {
    if (!isEditorVisible) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditorVisible]);

  // Once a fresh worksheet is exported for the first time and gets a real
  // database id, "graduate" the URL to the canonical saved-worksheet route
  // so a later refresh re-fetches it instead of losing it.
  const handleWorksheetPersisted = (worksheetId) => {
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;
    navigate(`/worksheet/${worksheetId}`, { replace: true });
  };

  // Passed to Navbar so any in-app navigation away from an in-progress
  // worksheet (nav links, logout) goes through the same "start over?"
  // confirmation as the explicit "New Worksheet" button.
  const handleNavigateAttempt = (proceed) => {
    if (!isEditorVisible) {
      proceed();
      return;
    }
    pendingNavigationRef.current = proceed;
    setShowStartOverWarning(true);
  };

  const handleKeepEditing = () => {
    setShowStartOverWarning(false);
    pendingNavigationRef.current = null;
  };

  // request: { prompt, region, difficulty, topics, yearGroup, count }
  const generateContentAndPdf = async (request, requestId = newRequestId()) => {
    setGenerationSettings(request);
    lastGenerationRef.current = () => generateContentAndPdf(request, requestId);
    setRetryMessage('');
    setAppStage('generating');
    setLoading(true);
    setError('');
    try {
      const response = await postAuthenticatedGeneration('/generate', request, { requestId, onRetry: showRetry });
      setFilteredQuestions(response.data);
      setAppStage('editing');
    } catch (err) {
      console.error('Error generating content:', err);
      setError(err.response?.data?.error || 'Error generating content. Please try again.');
      setAppStage('editing');
    } finally {
      setLoading(false);
    }
  };

  const generateColumnTemplatePdf = async (options) => {
    lastGenerationRef.current = () => generateColumnTemplatePdf(options);
    setRetryMessage('');
    setAppStage('generating');
    setLoading(true);
    setError('');
    try {
      const response = await postAuthenticatedGeneration('/generate-column', { options }, { onRetry: showRetry });
      setFilteredQuestions(response.data);
      setAppStage('editing');
    } catch (err) {
      console.error('Error generating column PDF:', err);
      setError('Error generating column worksheet. Please try again.');
      setAppStage('editing');
    } finally {
      setLoading(false);
    }
  };

  const generateConversionquestions = async (prompt, requestId = newRequestId()) => {
    lastGenerationRef.current = () => generateConversionquestions(prompt, requestId);
    setRetryMessage('');
    setAppStage('generating');
    setLoading(true);
    setError('');
    try {
      const response = await postAuthenticatedGeneration('/generate-conversion', { prompt }, { requestId, onRetry: showRetry });
      setFilteredQuestions(response.data);
      setAppStage('editing');
    } catch (err) {
      console.error('Error generating conversion questions:', err);
      setError(err.response?.data?.error || 'Error generating conversion worksheet. Please try again.');
      setAppStage('editing');
    } finally {
      setLoading(false);
    }
  };

  const generateCountingQuestionsHandler = (payload) => {
    lastGenerationRef.current = () => generateCountingQuestionsHandler(payload);
    setRetryMessage('');
    setAppStage('generating');
    setLoading(true);
    setError('');
    try {
      const questions = generateCountingQuestions({
        numQuestions: payload.options.numQuestions,
        maxPicturesPerCell: payload.options.maxPicturesPerCell,
        uploadedImages: payload.options.uploadedImages,
        selectedEmojis: payload.options.selectedEmojis,
      });
      setFilteredQuestions({
        template_id: 'counting-template',
        questions,
        questionsCount: payload.options.numQuestions,
      });
      setAppStage('editing');
    } catch (err) {
      console.error(err);
      setError('Error generating worksheet. Please try again.');
      setAppStage('editing');
    } finally {
      setLoading(false);
    }
  };

  const generatePictureOpsHandler = (payload) => {
    lastGenerationRef.current = () => generatePictureOpsHandler(payload);
    setRetryMessage('');
    setAppStage('generating');
    setLoading(true);
    setError('');
    try {
      const result = generatePicOpsQuestions(payload);
      setFilteredQuestions({
        template_id: 'picture-ops-template',
        questions: result.questions,
        questionsCount: result.questions.length,
      });
      setAppStage('editing');
    } catch (err) {
      console.error(err);
      setError('Error generating worksheet. Please try again.');
      setAppStage('editing');
    } finally {
      setLoading(false);
    }
  };

  const handlePromptGenerate = ({ raw, enriched, region, difficulty, topics, ...settings }) => {
    setUserPrompt(raw);
    setPromptParams({ region, difficulty, topics: topics || [], ...settings });
    setSelectedTemplate('exam-style');
    setPromptMode(true);
    generateContentAndPdf({ prompt: raw, region, difficulty, topics: topics || [], ...settings });

    if (user) {
      supabase.from('custom_prompts').insert({
        user_id: user.id,
        raw_prompt: raw,
        enriched_prompt: enriched,
        region: region ?? null,
        difficulty: difficulty ?? null,
        topics: topics?.length ? topics : null,
      }).then(({ error }) => {
        if (error) console.error('Failed to log custom prompt:', error);
      });
    }
  };

  const handleTemplateSelect = (id) => {
    navigate(`/create/${id}`);
  };

  const handleBack = () => {
    navigate('/create');
  };

  const handleRegenerate = () => {
    if (lastGenerationRef.current) {
      lastGenerationRef.current();
    }
  };

  const handleConfirmStartOver = () => {
    setShowStartOverWarning(false);
    const proceed = pendingNavigationRef.current;
    pendingNavigationRef.current = null;

    setFilteredQuestions([]);
    setSelectedTemplate(null);
    setUserPrompt('');
    setPromptParams({ region: null, difficulty: null, topics: [] });
    setPromptMode(false);
    setAppStage('initial');
    setError('');
    lastGenerationRef.current = null;
    hasRedirectedRef.current = false;

    if (proceed) {
      // Triggered by an intercepted nav-link click — go where they meant to.
      proceed();
    } else if (templateId) {
      // Triggered by the explicit "New Worksheet" button.
      navigate('/create');
    }
  };

  const defaultWorksheetTitle = promptMode
    ? (filteredQuestions?.title || 'Maths practice')
    : (TEMPLATE_NAMES[activeTemplateId] || 'Worksheet');

  return (
    <div className={isEditorVisible ? 'worksheet-page' : undefined}>
      <Navbar onNavigateAttempt={handleNavigateAttempt} />
      <div className="layout-body">
        <Popup />

        {showStartOverWarning && (
          <div className="start-over-overlay">
            <div className="start-over-modal">
              <p className="start-over-modal-title">Start a new worksheet?</p>
              <p className="start-over-modal-body">
                Your current worksheet and all edits will be lost. This cannot be undone.
              </p>
              <div className="start-over-modal-actions">
                <button
                  className="modal-btn modal-btn-cancel"
                  onClick={handleKeepEditing}
                >
                  Keep editing
                </button>
                <button className="modal-btn modal-btn-confirm" onClick={handleConfirmStartOver}>
                  Start over
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={`layout${!isEditorVisible ? ' layout-single-panel' : ''}`}>

          {!isEditorVisible && <div className="left-half">

            {appStage === 'initial' && (
              <div className="initial-panel">
                <div className="initial-header">
                  <p className="initial-title">Welcome back. Let’s create a worksheet.</p>
                  <p className="initial-subtitle">Start with your lesson, then choose the language and layout for your class.</p>
                </div>

                <div className="initial-prompt-section">
                  <p className="initial-section-label">Custom prompt</p>
                  <CustomPrompt onGenerate={handlePromptGenerate} loading={loading} initialText={userPrompt} initialRegion={promptParams.region} initialTopics={promptParams.topics} initialSettings={promptParams} />
                </div>

                {SHOW_TEMPLATE_CHOOSER && <div className="initial-template-section">
                  <p className="initial-section-label">or continue with a template</p>
                  <TemplateCarousel onSelectTemplate={handleTemplateSelect} />
                </div>}
              </div>
            )}

            {appStage === 'template-config' && (
              <div className="worksheet-setting-div">
                <p className="worksheet-setting-div-title">Worksheet Settings</p>
                <div className="back-to-templates-click" onClick={handleBack}>
                  <p>&larr; Back to Templates</p>
                </div>

                {activeTemplateId === 'exam-style' && (
                  <ExamRevision generateContentAndPdf={generateContentAndPdf} loading={loading} />
                )}
                {activeTemplateId === 'column-template' && (
                  <Columnquestions generateContentAndPdf={generateColumnTemplatePdf} loading={loading} />
                )}
                {activeTemplateId === 'conversion-template' && (
                  <Conversiontemplate generateContentAndPdf={generateConversionquestions} loading={loading} />
                )}
                {activeTemplateId === 'counting-template' && (
                  <CountingWorksheetTemplate
                    generateContentAndPdf={generateCountingQuestionsHandler}
                    loading={loading}
                  />
                )}
                {activeTemplateId === 'picture-ops-template' && (
                  <PictureOperations generateContentAndPdf={generatePictureOpsHandler} loading={loading} />
                )}

                {error && <p className="error-text">{error}</p>}
              </div>
            )}

            {isEditorVisible && (
              <div className="worksheet-info-panel">
                <p className="worksheet-setting-div-title">
                  {promptMode ? 'Custom Prompt' : (TEMPLATE_NAMES[activeTemplateId] || 'Worksheet')}
                </p>

                <div className="worksheet-info-content">
                  {promptMode ? (
                    // Reusing the same editable prompt box shown on the initial
                    // /create screen — pre-filled with what produced this
                    // worksheet, so tweaking the wording/curriculum/difficulty/
                    // topics and hitting Generate again regenerates in place,
                    // instead of only being able to replay the exact same prompt.
                    <CustomPrompt
                      onGenerate={handlePromptGenerate}
                      loading={loading}
                      initialText={userPrompt}
                      initialRegion={promptParams.region}
                      initialDifficulty={promptParams.difficulty}
                      initialTopics={promptParams.topics}
                      initialSettings={promptParams}
                      showSuggestions={false}
                    />
                  ) : (
                    <>
                      <div className="worksheet-info-prompt-box">
                        <p className="worksheet-info-label">Template</p>
                        <p className="worksheet-info-value">{TEMPLATE_NAMES[activeTemplateId]}</p>
                      </div>

                      <div className="worksheet-actions">
                        <button
                          className="action-btn regenerate-btn"
                          onClick={handleRegenerate}
                          disabled={loading}
                        >
                          {loading ? 'Generating…' : '↺  Regenerate'}
                        </button>
                        <button
                          className="action-btn start-over-btn"
                          onClick={() => setShowStartOverWarning(true)}
                          disabled={loading}
                        >
                          ← New Worksheet
                        </button>
                      </div>
                    </>
                  )}

                  {promptMode && (
                    <div className="worksheet-actions">
                      <button
                        className="action-btn start-over-btn"
                        onClick={() => setShowStartOverWarning(true)}
                        disabled={loading}
                      >
                        ← New Worksheet
                      </button>
                    </div>
                  )}

                  {error && <p className="error-text">{error}</p>}
                </div>
              </div>
            )}

          </div>}

          {isEditorVisible && (
            <div className="right-half">
              {error && <div className="generation-error" role="alert"><p>{error}</p><button onClick={handleRegenerate}>Try again</button><button onClick={() => setAppStage('initial')}>Edit prompt</button></div>}
              <div className={`editor-wrapper${loading ? ' editor-wrapper--loading' : ''}`}>
                {loading && (
                  <div className="editor-loading-overlay" role="status" aria-live="polite">
                    <div className="generation-orbit" />
                    <strong className="editor-loading-text">Creating your worksheet</strong><p role="status">{retryMessage || 'Generating questions and preparing your pages…'}</p>
                  </div>
                )}
                <div className={`worksheet-editor-content${loading ? ' editor-blur' : ''}`} inert={loading ? '' : undefined}>
                  <Texteditor
                    key={loading ? 'generating' : (filteredQuestions.generation_id || JSON.stringify(filteredQuestions))}
                    questions={loading ? [] : filteredQuestions.questions}
                    template_id={activeTemplateId}
                    worksheetTitle={defaultWorksheetTitle}
                    sourcePrompt={promptMode ? userPrompt : null}
                    onWorksheetPersisted={handleWorksheetPersisted}
                    generationId={filteredQuestions.generation_id ?? null}
                    generationQuality={filteredQuestions.quality ?? null}
                    generationSettings={generationSettings}
                  />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default Layout;
